<template>
  <div class="land-court-modal-shell">
    <div class="land-court-modal-header d-flex align-items-start justify-content-between gap-3">
      <div>
        <h5 class="mb-1 fw-bold text-white">Load Case</h5>
        <p class="mb-0 small text-white">
         Case record will be created.
        </p>
      </div>

      <button
        type="button"
        class="btn-close land-court-modal-close"
        aria-label="Close"
        @click="$emit('cancel')"
      ></button>
    </div>

    <div class="land-court-modal-body">
      <div class="land-court-modal-scroll pe-1 pe-md-2">
        <form @submit.prevent="submit_form">
          <div v-if="submitError" class="land-court-modal-error rounded-4 px-3 py-2 mb-3">
            {{ submitError }}
          </div>

          <div v-if="form_error && !no_active_form_available" class="land-court-modal-error rounded-4 px-3 py-2 mb-3">
            {{ form_error }}
          </div>

          <div class="row g-3">
            <div class="col-12">
              <label class="form-label land-court-modal-label">Main entity type *</label>
              <select
                :value="selectedEntityTypeId"
                class="form-select rounded-4 land-court-modal-select"
                @change="$emit('entity-type-change', Number($event.target.value))"
              >
                <option :value="''" disabled>Select entity type</option>
                <option
                  v-for="entity_type in entityTypeOptions"
                  :key="entity_type.id"
                  :value="entity_type.id"
                >
                  {{ entity_type.entity_type_name || entity_type.entity_type || `Entity type ${entity_type.id}` }}
                </option>
              </select>
            </div>

            <div v-if="loading_form" class="col-12">
              <div class="land-court-empty-state rounded-4 px-4 py-4 text-center">
                <div class="fw-semibold mb-1">Loading case form...</div>
                <div class="small text-secondary">Fetching the configured backend questions.</div>
              </div>
            </div>

            <div v-else-if="no_active_form_available" class="col-12">
              <div class="land-court-empty-state rounded-4 px-4 py-4 text-center">
                <div class="fw-semibold mb-1">No active form is currently configured for this case type.</div>
              </div>
            </div>

            <div v-else-if="!section_cards.length" class="col-12">
              <div class="land-court-empty-state rounded-4 px-4 py-4 text-center">
                <div class="fw-semibold mb-1">No form fields found</div>
                <div class="small text-secondary">Bind a form to this entity type to capture load-case details.</div>
              </div>
            </div>

            <template v-else>
              <div v-if="has_required_questions" class="col-12">
                <div class="alert alert-light border d-flex align-items-center gap-2 mb-1" role="alert">
                  <span class="text-danger fw-bold">*</span>
                </div>
              </div>

              <div
                v-for="section in section_cards"
                :key="section.id"
                class="col-12"
              >
                <div class="card shadow-sm mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">{{ section.name }}</h5>
                    <small v-if="section.description" class="text-muted">
                      {{ section.description }}
                    </small>
                  </div>

                  <div class="card-body">
                    <div class="row g-3">
                      <div
                        v-for="question in section.questions"
                        v-show="is_question_visible(question)"
                        :key="question_key(question)"
                        class="col-12"
                        :data-question-key="question_key(question)"
                      >
                        <label class="form-label land-court-modal-label fw-semibold">
                          {{ question.question_text }}
                          <span v-if="is_question_required(question)" class="text-danger fw-bold fs-5 ms-1">*</span>
                        </label>

                        <select
                          v-if="is_data_selection_question(question)"
                          :value="get_answer_value(question)"
                          class="form-select rounded-4 land-court-modal-select"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                          @change="update_answer_value(question, $event.target.value)"
                        >
                          <option value="">Select an option</option>
                          <option
                            v-for="option in data_selection_question_options(question)"
                            :key="option"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>

                        <select
                          v-else-if="is_select_question(question) && !is_multi_select_question(question)"
                          :value="get_answer_value(question)"
                          class="form-select rounded-4 land-court-modal-select"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                          @change="update_answer_value(question, $event.target.value)"
                        >
                          <option value="">Select an option</option>
                          <option
                            v-for="option in question.multi_select_choices"
                            :key="option.id || option.choice || option"
                            :value="option.choice || option"
                          >
                            {{ option.choice || option }}
                          </option>
                        </select>

                        <select
                          v-else-if="is_multi_select_question(question)"
                          :value="get_multi_answer_value(question)"
                          class="form-select rounded-4 land-court-modal-select"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                          multiple
                          @change="update_multi_answer_value(question, $event.target.selectedOptions)"
                        >
                          <option
                            v-for="option in question.multi_select_choices"
                            :key="option.id || option.choice || option"
                            :value="option.choice || option"
                          >
                            {{ option.choice || option }}
                          </option>
                        </select>

                        <div
                          v-else-if="is_checkbox_question(question)"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                        >
                          <div
                            v-for="option in question.multi_select_choices"
                            :key="option.id || option.choice || option"
                            class="form-check"
                          >
                            <input
                              :id="`${question_key(question)}-${option.id || option.choice || option}`"
                              class="form-check-input"
                              type="checkbox"
                              :value="option.choice || option"
                              :checked="get_multi_answer_value(question).includes(option.choice || option)"
                              @change="update_checkbox_answer_value(question, option.choice || option, $event.target.checked)"
                            >
                            <label
                              class="form-check-label"
                              :for="`${question_key(question)}-${option.id || option.choice || option}`"
                            >
                              {{ option.choice || option }}
                            </label>
                          </div>
                        </div>

                        <div
                          v-else-if="is_radio_question(question)"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                        >
                          <div
                            v-for="option in question.multi_select_choices"
                            :key="option.id || option.choice || option"
                            class="form-check"
                          >
                            <input
                              :id="`${question_key(question)}-${option.id || option.choice || option}`"
                              class="form-check-input"
                              type="radio"
                              :name="question_key(question)"
                              :value="option.choice || option"
                              :checked="get_answer_value(question) === (option.choice || option)"
                              @change="update_answer_value(question, $event.target.value)"
                            >
                            <label
                              class="form-check-label"
                              :for="`${question_key(question)}-${option.id || option.choice || option}`"
                            >
                              {{ option.choice || option }}
                            </label>
                          </div>
                        </div>

                        <textarea
                          v-else-if="is_long_text_question(question)"
                          :value="get_answer_value(question)"
                          class="form-control land-court-modal-input land-court-modal-textarea rounded-4"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                          rows="4"
                          :placeholder="question.question_description || ''"
                          @input="update_answer_value(question, $event.target.value)"
                        ></textarea>

                        <input
                          v-else-if="is_file_question(question)"
                          type="file"
                          class="form-control land-court-modal-input rounded-4"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                          @change="update_file_answer_value(question, $event.target.files)"
                        >

                        <input
                          v-else
                          :value="get_answer_value(question)"
                          :type="input_type_for_question(question)"
                          class="form-control land-court-modal-input rounded-4"
                          :class="{ 'land-court-modal-input-invalid': has_question_error(question) }"
                          :placeholder="question.question_description || ''"
                          @input="update_answer_value(question, $event.target.value)"
                        >

                        <div v-if="has_question_error(question)" class="small text-danger mt-2 fw-semibold">
                          {{ question_error_message(question) }}
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="land-court-modal-footer d-flex justify-content-end gap-2 mt-4">
            <div
              v-if="form_error && !no_active_form_available"
              class="land-court-modal-error rounded-4 px-3 py-2 flex-grow-1"
            >
              {{ form_error }}
            </div>
            <button
              type="submit"
              class="btn land-court-submit-btn rounded"
              :disabled="submitting || loading_form || !section_cards.length"
            >
              <i class="bi bi-plus me-1"></i>
              {{ submitting ? 'Saving...' : 'Add to caseload' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { fetch_land_court_form_details } from '@/services/land_court_service';

function normalize_text(value) {
  return String(value || '').trim();
}

const NO_ACTIVE_FORM_MESSAGE = 'No active form is currently configured for this case type.';
const MANDATORY_FIELDS_MESSAGE = 'Please complete all mandatory fields before submitting.';
const EMPTY_FORM_MESSAGE = 'Please complete at least one field before submitting.';

function normalize_label(value) {
  return normalize_text(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function question_key(question = {}) {
  return String(question.section_question_id || question.question_id || question.id || question.question_text || '');
}

function keyed_record(records, key) {
  const normalized_key = key === null || key === undefined ? '' : String(key);
  if (!normalized_key) return null;

  const source_records = Array.isArray(records) ? records : [];
  for (const record of source_records) {
    if (record.key === normalized_key) {
      return record;
    }
  }

  return null;
}

function is_existing_legal_representative_question_label(value = '') {
  const normalized_label = normalize_label(value);
  return (
    normalized_label.startsWith('select ')
    && normalized_label.includes('existing')
    && normalized_label.includes('legal representative')
    && (
      normalized_label.includes('applicant')
      || normalized_label.includes('landowner')
      || normalized_label.includes('respondent')
    )
  );
}

function first_order_value(item = {}, fallback_order = 0) {
  const raw_value = [
    item?.order_number,
    item?.order,
    item?.sort_order,
    item?.question_number,
    item?.position,
    item?.sequence,
    fallback_order,
  ].find((value) => Number.isFinite(Number(value)) && Number(value) > 0);

  return Number(raw_value || fallback_order || 0);
}

function item_identity(item = {}, fallback_identity = 0) {
  return Number(
    item?.section_question_id
    || item?.question_id
    || item?.section_id
    || item?.main_section_id
    || item?.id
    || fallback_identity
    || 0
  );
}

function sorted_items(items = []) {
  return [...items].sort((left, right) => {
    const left_order = first_order_value(left, Number.MAX_SAFE_INTEGER);
    const right_order = first_order_value(right, Number.MAX_SAFE_INTEGER);

    if (left_order !== right_order) {
      return left_order - right_order;
    }

    return item_identity(left) - item_identity(right);
  });
}

function data_selection_values_by_type(data_selection_options = {}, option_type = '') {
  switch (option_type) {
    case 'farms':
      return data_selection_options.farms;
    case 'representatives':
      return data_selection_options.representatives;
    default:
      return [];
  }
}

function strict_boolean_true(value) {
  const normalized_value = normalize_text(value).toLowerCase();
  return value === true
    || value === 1
    || normalized_value === 'true'
    || normalized_value === '1'
    || normalized_value === 'yes'
    || normalized_value === 'required'
    || normalized_value === 'mandatory';
}

function question_required_value(question = {}) {
  return [
    question.is_required,
    question.required,
    question.is_mandatory,
    question.mandatory,
    question.is_compulsory,
    question.compulsory,
    question.question?.is_required,
    question.question?.required,
    question.question?.is_mandatory,
    question.question?.mandatory,
  ].some(strict_boolean_true);
}

export default {
  name: 'LoadCaseDynamicForm',
  props: {
    caseContext: {
      type: Object,
      default: () => ({})
    },
    entityTypeOptions: {
      type: Array,
      default: () => []
    },
    selectedEntityTypeId: {
      type: [Number, String],
      default: null
    },
    submitError: {
      type: String,
      default: ''
    },
    submitting: {
      type: Boolean,
      default: false
    },
    dataSelectionOptions: {
      type: Object,
      default: () => ({
        farms: [],
        representatives: []
      })
    }
  },
  emits: ['cancel', 'entity-type-change', 'submit'],
  data() {
    return {
      loading_form: false,
      form_error: '',
      form_details: {},
      answer_values: [],
      validation_errors: [],
    };
  },
  computed: {
    resolved_form_id() {
      const form_ids = Array.isArray(this.caseContext?.form_ids) ? this.caseContext.form_ids : [];
      return Number(form_ids[0] || 0) || null;
    },
    ordered_sections() {
      const raw_sections = Array.isArray(this.form_details?.main_sections)
        ? this.form_details.main_sections
        : Array.isArray(this.form_details?.sections)
          ? [{
              id: this.form_details?.id || 'main-section-1',
              main_section_name: this.form_details?.form_name || 'Case details',
              description: this.form_details?.form_description || '',
              order_number: 1,
              sub_sections: this.form_details.sections,
            }]
          : [];

      return sorted_items(raw_sections).map((main_section) => ({
        ...main_section,
        main_section_name: normalize_text(main_section?.main_section_name || main_section?.name || 'Section'),
        description: normalize_text(main_section?.description || ''),
        sub_sections: sorted_items(main_section?.sub_sections || []).map((sub_section) => ({
          ...sub_section,
          section_name: normalize_text(sub_section?.section_name || sub_section?.name || ''),
          description: normalize_text(sub_section.description || sub_section.section_description || ''),
          questions: sorted_items(sub_section?.questions || []).map((question) => ({
            ...question,
            question_text: normalize_text(question?.question_text || 'Untitled question'),
            question_description: normalize_text(question?.question_description || ''),
            question_type: normalize_text(
              question?.question_type?.question_type
              || question?.question_type?.type
              || question?.question_type
            ).toLowerCase(),
            is_required: question_required_value(question),
            multi_select_choices: Array.isArray(question?.multi_select_choices || question?.options)
              ? sorted_items(question.multi_select_choices || question.options)
              : [],
          })),
        })),
      }));
    },
    section_cards() {
      return this.ordered_sections.flatMap((main_section) => {
        const sub_sections = Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [];

        return sub_sections
          .filter((sub_section) => Array.isArray(sub_section?.questions) && sub_section.questions.length)
          .map((sub_section) => ({
            id: `${main_section.id || main_section.main_section_id || 'main'}-${sub_section.id || sub_section.section_id || sub_section.section_name}`,
            name: normalize_text(sub_section?.section_name || main_section?.main_section_name || 'Section'),
            description: normalize_text(sub_section?.description || ''),
            questions: sub_section.questions,
          }));
      });
    },
    no_active_form_available() {
      return !this.resolved_form_id || this.form_error === NO_ACTIVE_FORM_MESSAGE;
    },
    has_required_questions() {
      return this.section_cards
        .flatMap((section) => section.questions || [])
        .some((question) => this.is_question_required(question));
    },
    has_any_answer() {
      return this.answer_values.some(({ value }) => {
        if (typeof File !== 'undefined' && value instanceof File) {
          return true;
        }
        if (Array.isArray(value)) {
          return value.some((entry) => normalize_text(entry));
        }
        return Boolean(normalize_text(value));
      });
    },
  },
  watch: {
    resolved_form_id: {
      immediate: true,
      async handler() {
        await this.load_form_details();
      }
    }
  },
  methods: {
    question_key(question = {}) {
      return question_key(question);
    },
    answer_value_by_key(key) {
      return keyed_record(this.answer_values, key)?.value;
    },
    set_answer_value_by_key(key, value) {
      const normalized_key = String(key || '');
      if (!normalized_key) return;

      const next_record = { key: normalized_key, value };
      const current_values = Array.isArray(this.answer_values) ? this.answer_values : [];
      const next_values = [];
      let updated_existing_record = false;

      for (const record of current_values) {
        if (record.key === normalized_key) {
          next_values.push(next_record);
          updated_existing_record = true;
        } else {
          next_values.push(record);
        }
      }

      if (!updated_existing_record) {
        next_values.push(next_record);
      }

      this.answer_values = next_values;
    },
    validation_error_by_key(key) {
      return keyed_record(this.validation_errors, key)?.message ?? '';
    },
    set_validation_errors(errors) {
      this.validation_errors = Array.isArray(errors) ? errors : [];
    },
    remove_validation_error_by_key(key) {
      const normalized_key = String(key || '');
      this.validation_errors = this.validation_errors
        .filter((record) => record.key !== normalized_key);
    },
    async load_form_details() {
      this.form_error = '';
      this.form_details = {};
      this.answer_values = [];
      this.validation_errors = [];

      if (!this.resolved_form_id) {
        return;
      }

      this.loading_form = true;

      try {
        const form_details = await fetch_land_court_form_details(this.resolved_form_id);
        if (form_details?.is_active === false) {
          this.form_error = NO_ACTIVE_FORM_MESSAGE;
          this.form_details = {};
          return;
        }

        this.form_details = form_details;
      } catch (error) {
        this.form_error =
          error?.response?.data?.message
          || error?.message
          || 'Failed to load the backend form definition.';
      } finally {
        this.loading_form = false;
      }
    },
    input_type_for_question(question) {
      const question_type = normalize_text(question?.question_type).toLowerCase();
      if (question_type.includes('date')) {
        return 'date';
      }
      if (question_type.includes('number')) {
        return 'number';
      }
      return 'text';
    },
    is_select_question(question) {
      return normalize_text(question?.question_type).includes('select');
    },
    is_multi_select_question(question) {
      return normalize_text(question?.question_type).replace(/\s+/g, '_').includes('multi_select');
    },
    is_checkbox_question(question) {
      return normalize_text(question?.question_type).toLowerCase().includes('checkbox');
    },
    is_radio_question(question) {
      return normalize_text(question?.question_type).toLowerCase().includes('radio');
    },
    is_single_select_question(question) {
      return this.is_select_question(question) && !this.is_multi_select_question(question);
    },
    is_long_text_question(question) {
      const question_type = normalize_text(question?.question_type).toLowerCase();
      return question_type.includes('long') || question_type.includes('text_area') || question_type.includes('textarea');
    },
    is_file_question(question) {
      return normalize_text(question?.question_type).toLowerCase().includes('file');
    },
    data_selection_question_type(question = {}) {
      if (!this.is_single_select_question(question)) {
        return '';
      }

      const normalized_question_label = normalize_label(question.question_text);

      if (
        normalized_question_label === 'select from existing farms'
      ) {
        return 'farms';
      }

      if (is_existing_legal_representative_question_label(normalized_question_label)) {
        return 'representatives';
      }

      return '';
    },
    data_selection_question_options(question = {}) {
      const option_type = this.data_selection_question_type(question);
      if (!option_type) {
        return [];
      }

      const selected_values = data_selection_values_by_type(this.dataSelectionOptions, option_type);
      const values = Array.isArray(selected_values)
        ? selected_values
        : [];

      return [...new Set(values.map((value) => normalize_text(value)).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right));
    },
    is_data_selection_question(question = {}) {
      return Boolean(this.data_selection_question_type(question));
    },
    is_question_required(question = {}) {
      return question_required_value(question);
    },
    parent_question_key(question = {}) {
      const parent_id = [
        question.parent_section_question_id,
        question.parent_question_id,
        question.main_section_question_id,
        question.main_question_id,
        question.depends_on_section_question_id,
        question.depends_on_question_id,
      ].find((value) => Number(value || 0) > 0);

      if (parent_id) {
        return String(parent_id);
      }

      const parent_text = normalize_text(
        question.parent_question_text
        || question.main_question_text
        || question.depends_on_question_text
      );

      if (!parent_text) {
        return '';
      }

      const parent_question = this.section_cards
        .flatMap((section) => section.questions || [])
        .find((candidate) => normalize_label(candidate?.question_text) === normalize_label(parent_text));

      return parent_question ? question_key(parent_question) : '';
    },
    trigger_condition_value(question = {}) {
      return normalize_text(
        question.trigger_condition
        || question.condition_value
        || question.show_when_value
        || question.depends_on_value
      );
    },
    is_question_visible(question = {}) {
      if (question.is_visible === false || question.hidden === true || question.is_hidden === true) {
        return false;
      }

      const trigger_condition = this.trigger_condition_value(question);
      const parent_key = this.parent_question_key(question);
      if (!trigger_condition || !parent_key) {
        return true;
      }

      const parent_answer = this.answer_value_by_key(parent_key);
      if (Array.isArray(parent_answer)) {
        return parent_answer.map(normalize_label).includes(normalize_label(trigger_condition));
      }

      return normalize_label(parent_answer) === normalize_label(trigger_condition);
    },
    get_answer_value(question) {
      return this.answer_value_by_key(question_key(question)) || '';
    },
    get_multi_answer_value(question) {
      const current_value = this.answer_value_by_key(question_key(question));
      return Array.isArray(current_value) ? current_value : [];
    },
    has_question_error(question) {
      return Boolean(this.validation_error_by_key(question_key(question)));
    },
    question_error_message(question) {
      return this.validation_error_by_key(question_key(question));
    },
    update_answer_value(question, value) {
      this.set_answer_value_by_key(question_key(question), value);
      this.clear_question_error(question);
      this.clear_form_error_if_resolved();
    },
    update_multi_answer_value(question, selected_options) {
      this.set_answer_value_by_key(
        question_key(question),
        Array.from(selected_options || []).map((option) => option.value)
      );
      this.clear_question_error(question);
      this.clear_form_error_if_resolved();
    },
    update_checkbox_answer_value(question, option_value, is_checked) {
      const current_values = this.get_multi_answer_value(question);
      const next_values = is_checked
        ? [...new Set([...current_values, option_value])]
        : current_values.filter((value) => value !== option_value);

      this.set_answer_value_by_key(question_key(question), next_values);
      this.clear_question_error(question);
      this.clear_form_error_if_resolved();
    },
    update_file_answer_value(question, files) {
      const [selected_file] = Array.from(files || []);
      this.set_answer_value_by_key(question_key(question), selected_file || null);
      this.clear_question_error(question);
      this.clear_form_error_if_resolved();
    },
    clear_question_error(question) {
      if (!this.has_question_error(question)) {
        return;
      }

      if (this.is_question_required(question) && this.is_answer_empty(question, this.get_answer_value(question))) {
        return;
      }

      this.remove_validation_error_by_key(question_key(question));
      this.clear_form_error_if_resolved();
    },
    clear_form_error_if_resolved() {
      if (this.form_error === EMPTY_FORM_MESSAGE && this.has_any_answer) {
        this.form_error = '';
        return;
      }

      if (this.form_error === MANDATORY_FIELDS_MESSAGE && !this.validation_errors.length) {
        this.form_error = '';
      }
    },
    is_answer_empty(question, answer) {
      if (typeof File !== 'undefined' && answer instanceof File) {
        return false;
      }
      if (Array.isArray(answer)) {
        return !answer.some((entry) => normalize_text(entry));
      }
      if (this.is_file_question(question)) {
        return !answer;
      }
      return !normalize_text(answer);
    },
    validate_dynamic_form() {
      const next_errors = [];
      const visible_questions = this.section_cards
        .flatMap((section) => section.questions || [])
        .filter((question) => this.is_question_visible(question));

      visible_questions
        .filter((question) => this.is_question_required(question))
        .forEach((question) => {
          const key = question_key(question);
          const answer = this.answer_value_by_key(key);

          if (this.is_answer_empty(question, answer)) {
            next_errors.push({
              key,
              message: `"${question.question_text}" is required.`
            });
          }
        });

      if (!next_errors.length && !this.has_any_answer) {
        const [first_visible_question] = visible_questions;
        if (first_visible_question) {
          next_errors.push({
            key: question_key(first_visible_question),
            message: EMPTY_FORM_MESSAGE
          });
        }
        this.set_validation_errors(next_errors);
        this.form_error = EMPTY_FORM_MESSAGE;
        return false;
      }

      this.set_validation_errors(next_errors);
      this.form_error = next_errors.length
        ? MANDATORY_FIELDS_MESSAGE
        : '';

      return !next_errors.length;
    },
    scroll_to_first_error() {
      const first_error_key = this.validation_errors[0]?.key || '';
      if (!first_error_key || typeof document === 'undefined') {
        return;
      }

      this.$nextTick(() => {
        const question_element = Array.from(document.querySelectorAll('[data-question-key]'))
          .find((element) => element.getAttribute('data-question-key') === first_error_key);
        if (!question_element) {
          return;
        }

        question_element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = question_element.querySelector('input, select, textarea, button');
        if (focusable && typeof focusable.focus === 'function') {
          focusable.focus({ preventScroll: true });
        }
      });
    },
    build_submission_answers() {
      return this.ordered_sections.flatMap((main_section) =>
        (main_section.sub_sections || []).flatMap((sub_section) =>
          (sub_section.questions || [])
            .filter((question) => this.is_question_visible(question))
            .map((question) => {
            const raw_value = this.answer_value_by_key(question_key(question));
            const is_file_value = typeof File !== 'undefined' && raw_value instanceof File;
            const answer_text = is_file_value
              ? raw_value.name
              : Array.isArray(raw_value)
                ? raw_value.join(', ')
                : raw_value;

            return {
              section_question_id: Number(question?.section_question_id || 0) || null,
              question_id: Number(question?.question_id || question?.id || 0) || null,
              question_text: question.question_text,
              answer_text: normalize_text(answer_text),
              file: is_file_value ? raw_value : null,
              is_mandatory: this.is_question_required(question),
            };
          })
        )
      );
    },
    submit_form() {
      this.validation_errors = [];

      if (!this.validate_dynamic_form()) {
        this.scroll_to_first_error();
        return;
      }

      const answers = this.build_submission_answers();
      this.form_error = '';
      this.$emit('submit', {
        form_id: this.resolved_form_id,
        form_name: this.form_details?.form_name || '',
        sections: this.ordered_sections,
        answers,
      });
    },
  },
};
</script>
